import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, X, Headphones, User, FileText, MessageSquare, Loader2 } from 'lucide-react';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import { useLiveSupport } from '../contexts/LiveSupportContext';

interface LiveSupportModalProps {
  onClose: () => void;
  onSuccess: () => void;
  onBack: () => void;
}

const LiveSupportModal: React.FC<LiveSupportModalProps> = ({ onClose, onSuccess, onBack }) => {
  const { currentPlayer } = useSarLabAuth();
  const { createTicket } = useLiveSupport();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!subject.trim()) {
      setError('Lütfen bir konu seçin veya yazın');
      return;
    }

    if (!message.trim()) {
      setError('Lütfen bir mesaj yazın');
      return;
    }

    if (message.trim().length < 10) {
      setError('Mesajınız en az 10 karakter olmalıdır');
      return;
    }

    setIsLoading(true);

    try {
      await createTicket(subject, message);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Backend enum değerleri ve Türkçe karşılıkları
  const subjectOptions = [
    { value: 'payment', label: 'Ödeme Sorunu' },
    { value: 'account', label: 'Hesap Sorunu' },
    { value: 'technical', label: 'Teknik Destek' },
    { value: 'bonus', label: 'Bonus ve Promosyonlar' },
    { value: 'game', label: 'Oyun Sorunu' },
    { value: 'other', label: 'Diğer' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
              <Headphones className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium text-white">Canlı Destek</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* User Info */}
        <div className="bg-dark-700/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-sm text-gray-400 mb-1">
            <User className="w-4 h-4" />
            <span>Kullanıcı Adı</span>
          </div>
          <p className="text-white font-medium">{currentPlayer?.username || 'Kullanıcı'}</p>
        </div>

        {/* Subject */}
        <div>
          <label className="flex items-center space-x-2 text-sm text-gray-400 mb-2">
            <FileText className="w-4 h-4" />
            <span>Ne için destek almak istiyorsunuz?</span>
          </label>
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">Konu Seçin</option>
            {subjectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <label className="flex items-center space-x-2 text-sm text-gray-400 mb-2">
            <MessageSquare className="w-4 h-4" />
            <span>Mesajınız</span>
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Sorununuzu kısaca açıklayın..."
            rows={4}
            className="w-full px-3 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-2 rounded-lg text-sm"
          >
            {error}
          </motion.div>
        )}

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Bağlanıyor...</span>
            </>
          ) : (
            <>
              <Headphones className="w-4 h-4" />
              <span>Canlı Desteğe Bağlan</span>
            </>
          )}
        </motion.button>

        {/* Info Text */}
        <p className="text-xs text-gray-500 text-center">
          Destek ekibimiz en kısa sürede size yardımcı olacaktır.
        </p>
      </form>
    </div>
  );
};

export default LiveSupportModal;
